import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Role } from "@/constants/rolePermissions";
import { EnrichedTask } from "@/hooks/admin/AdminTodosTabs/useTasks";
import { getStatusIcon } from "@/utils/shared/getStatusIcon";
import { getStatusText } from "@/utils/shared/getStatusText";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { BookCheck, ClipboardList, RotateCcw } from "lucide-react";
import { useState } from "react";

interface CleaningStaffCardProps {
  user: any;
  tasks: EnrichedTask[];
  onAssign: (user: any) => void;
  onResendInvitation: (userId: string) => void;
  isLoading?: boolean;
  role?: Role;
}

const CleaningStaffCard = ({
  user,
  tasks,
  onAssign,
  onResendInvitation,
  isLoading = false,
  role,
}: CleaningStaffCardProps) => {
  const userTasks = tasks.filter(
    (task) => task.assigned_user_id === user.user_id,
  );

  const [showAllTasks, setShowAllTasks] = useState(false);
  const visibleTasks = showAllTasks ? userTasks : userTasks.slice(0, 5);

  return (
    <Card className="transition-shadow hover:shadow-md rounded-xl border border-gray-200">
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Columna izquierda */}
          <div className="space-y-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {user.first_name} {user.last_name}
              </h3>
              <p className="text-sm text-gray-500">{user.user_id}</p>
            </div>

            <div className="text-sm text-gray-700 space-y-3">
              <p>
                <span className="font-medium">Teléfono:</span>{" "}
                {user.phone || (
                  <span className="text-gray-400">No especificado</span>
                )}
              </p>

              <p>
                <span className="font-medium">Creado:</span>{" "}
                {user.created_at
                  ? format(new Date(user.created_at), "dd/MM/yy", {
                      locale: es,
                    })
                  : "—"}
              </p>

              <div className="flex items-center gap-2 mt-2">
                {getStatusIcon(user.status)}
                <span className="font-medium">
                  {getStatusText(user.status)}
                </span>
              </div>
            </div>
          </div>

          {/* Columna derecha: tareas */}
          <div className="space-y-3 flex flex-col justify-between">
            <div className="space-y-1">
              <p className="font-medium text-sm text-gray-700">
                Tareas asignadas ({userTasks.length})
              </p>

              {userTasks.length === 0 ? (
                <p className="text-sm text-gray-400">
                  No hay tareas asignadas.
                </p>
              ) : (
                <>
                  <ul className="space-y-1 text-sm text-gray-800">
                    {visibleTasks.map((task) => (
                      <li key={task.id} className="flex items-center gap-2">
                        <BookCheck className="w-4 h-4 text-gray-500" />
                        {task.title}
                      </li>
                    ))}
                  </ul>

                  {userTasks.length > 5 && (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-sm text-brand-primary px-0"
                      onClick={() => setShowAllTasks(!showAllTasks)}
                    >
                      {showAllTasks ? "Ver menos" : "Ver más"}
                    </Button>
                  )}
                </>
              )}
            </div>

            <div className="flex gap-2">
              {onAssign && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAssign(user)}
                >
                  <ClipboardList className="w-4 h-4 mr-1" />
                  Asignar tarea
                </Button>
              )}

              {(user.status === "pending" || user.status === "expired") && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onResendInvitation(user.user_id)}
                  disabled={isLoading}
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reenviar
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CleaningStaffCard;
